import {
  Button,
  EmptyState,
  FlexBox,
  Icon,
  IconButton,
  Spinner,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { useEffect, useState } from 'react';

import { templatesApi } from 'apis/templates';
import {
  convertPlainToHtml,
  getDirectionFormattedHtml,
} from 'app/utils/htmlFormatting';
// eslint-disable-next-line no-restricted-imports
import { SafeInnerHtmlSetter } from 'components/shared/util/SafeInnerHtmlSetter';

import {
  getLanguageSpecificContent,
  getLanguageSpecificSubject,
} from '../../util/getLanguageSpecificContent';
import { EnhancedTemplate } from '../../util/useQuickReplyData';

import styles from './quickRepliesPreview.module.scss';
import { disableTab } from './utils/disableTab';
import { templateDiff } from './utils/templateDiff';

type QuickRepliesPreviewProps = {
  template: EnhancedTemplate | undefined;
  csid: number | null;
  enduserId?: string | null;
  language: string;
  isLoading: boolean;
  /**
   * Callback to insert the contents of the selected template into the
   * editor/form.
   */
  onApplyTemplate: () => void;
  showAutomation: boolean;
};

enum TemplatePreviewState {
  TEMPLATE = 0,
  LOADING = 1,
  NO_CONTENT_AVAILABLE = 2,
  ERROR = 3,
}

export const QuickRepliesPreview = ({
  template,
  csid,
  enduserId = null,
  language,
  isLoading,
  onApplyTemplate,
  showAutomation,
}: QuickRepliesPreviewProps): JSX.Element | null => {
  const name = template?.name;
  const isRecommended = template?.sourceList === 'recommendations';
  const [feedbackSent, setFeedbackSent] = useState<boolean>(false);
  const templateHasAutomation = !!template?.automation_id;

  const preRenderContent = getLanguageSpecificContent(language, template) || '';
  const [renderTemplate, renderResult] =
    templatesApi.useRenderTemplateMutation();

  useEffect(() => {
    if (template && !isLoading) {
      renderTemplate({
        templateId: template.id,
        locale: language,
        csid,
        subject: getLanguageSpecificSubject(language, template) || null,
        enduserId,
      });
    }
  }, [template, language, csid, isLoading, renderTemplate, enduserId]);

  const state = (() => {
    if (renderResult.isError) return TemplatePreviewState.ERROR;
    if (renderResult.isLoading) return TemplatePreviewState.LOADING;
    if (!template) return TemplatePreviewState.NO_CONTENT_AVAILABLE;
    return TemplatePreviewState.TEMPLATE;
  })();

  const renderedContent = disableTab(
    getDirectionFormattedHtml(
      convertPlainToHtml(
        renderResult.data?.data.rendered
          ? templateDiff(
              preRenderContent,
              renderResult.data.data.rendered,
              styles.highlight,
            )
          : preRenderContent,
      ),
    ),
  );

  return (
    <FlexBox
      borderWidth="0 0 0 1px"
      borderColor="neutral.200"
      padding="300"
      height="320px"
      width="360px"
      flexDirection="column"
      justifyContent="center"
    >
      {state === TemplatePreviewState.LOADING && (
        <FlexBox flexDirection="column" gap="8px" alignItems="center">
          <Spinner size={32} />
          <Text.Paragraph variant="2">Loading template...</Text.Paragraph>
        </FlexBox>
      )}
      {state === TemplatePreviewState.NO_CONTENT_AVAILABLE && (
        <EmptyState
          title="No content available"
          description={
            <FlexBox flexDirection="column" gap="300">
              <Text.Paragraph>No template is available</Text.Paragraph>
            </FlexBox>
          }
          icon="ban"
        />
      )}
      {state === TemplatePreviewState.ERROR && (
        <EmptyState
          title="Unable to render template"
          description={
            <FlexBox flexDirection="column" gap="300">
              <Text.Paragraph>
                There was an error rendering the template
              </Text.Paragraph>
            </FlexBox>
          }
          icon="ban"
        />
      )}
      {state === TemplatePreviewState.TEMPLATE && (
        <>
          <FlexBox flexDirection="column">
            <Text.Heading variant="heading-2" className={styles.title}>
              {name}
              {showAutomation && templateHasAutomation && (
                <Tooltip content="Using this template will also trigger an associated automation.">
                  <Icon name="flow" className={styles.automation} />
                </Tooltip>
              )}
            </Text.Heading>
            <Text.Paragraph variant="4" className={styles.explainer}>
              The following text will be added:
            </Text.Paragraph>
          </FlexBox>
          <FlexBox flex="1 1 auto" className={styles.content}>
            <SafeInnerHtmlSetter
              element="div"
              profile="email"
              className={styles.safeContent}
              content={renderedContent}
            />
          </FlexBox>
          <FlexBox
            flex="0 0 auto"
            justifyContent="space-between"
            className={styles.actions}
          >
            <FlexBox alignContent="center">
              {isRecommended &&
                (feedbackSent ? (
                  <Text.Paragraph variant="4" className={styles.feedbackSent}>
                    Thank you for your feedback!
                  </Text.Paragraph>
                ) : (
                  <>
                    <IconButton
                      icon="thumbs-up"
                      variant="tertiary"
                      onClick={() => {
                        ampli.quickReplyFeedbackForRecommendedRepliesClicked({
                          feedback: 'good',
                          score: template?.score,
                        });
                        setFeedbackSent(true);
                      }}
                      tabIndex={-1}
                    />
                    <IconButton
                      icon="thumbs-down"
                      variant="tertiary"
                      onClick={() => {
                        ampli.quickReplyFeedbackForRecommendedRepliesClicked({
                          feedback: 'bad',
                          score: template?.score,
                        });
                        setFeedbackSent(true);
                      }}
                      tabIndex={-1}
                    />
                  </>
                ))}
            </FlexBox>
            <Button variant="primary" onClick={onApplyTemplate}>
              Use this reply
            </Button>
          </FlexBox>
        </>
      )}
    </FlexBox>
  );
};
